import {useAlive} from '@github-ui/use-alive'
import {throttle} from '@github/mini-throttle'
import useIsMounted from '@github-ui/use-is-mounted'
import {useCallback, useMemo} from 'react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {BACKGROUND_REFETCH_THROTTLE_INTERVAL_IN_MS, FOREGROUND_REFETCH_THROTTLE_INTERVAL_IN_MS} from '../constants'

import {useStatusChecksPageData} from '../page-data/loaders/use-status-checks-page-data'
import {usePageVisibility} from './use-page-visibility'

export function useStatusChecksLiveUpdates(channel: string, pullRequestHeadSha: string): void {
  const {refetch, invalidate} = useStatusChecksPageData({pullRequestHeadSha})
  const isMounted = useIsMounted()
  const isPageVisible = usePageVisibility()
  const dedupeConcurrentRefetches = isFeatureEnabled('merge_status_checks_refetch_dedupe')

  const throttleTiming = isPageVisible
    ? FOREGROUND_REFETCH_THROTTLE_INTERVAL_IN_MS
    : BACKGROUND_REFETCH_THROTTLE_INTERVAL_IN_MS

  // Throttle a query-level invalidation so concurrent updates across instances collapse into a single refetch.
  const throttledInvalidate = useMemo(
    () =>
      throttle(() => {
        if (isMounted()) {
          invalidate()
        }
      }, throttleTiming),
    [isMounted, invalidate, throttleTiming],
  )

  // Each hook instance owns its own throttle and refetches via its own query observer.
  const throttledRefetch = useMemo(
    () =>
      throttle(() => {
        if (isMounted()) {
          refetch()
        }
      }, throttleTiming),
    [isMounted, refetch, throttleTiming],
  )

  const handleNotification = useCallback(
    (data: {wait?: number}) => {
      // This creates a 2-10s throttle to prevent multiple refetches happening in the same < 500ms time window
      const throttledRefresh = dedupeConcurrentRefetches ? throttledInvalidate : throttledRefetch
      window.setTimeout(throttledRefresh, data.wait || 0)
    },
    [dedupeConcurrentRefetches, throttledInvalidate, throttledRefetch],
  )

  useAlive(channel, handleNotification)
}
